import _ from 'underscore'
import LyraSentryClient from '@lyra-network/lyra-common-sentry-client/dist/lyra-sentry-client.umd.js'

import SentryDomains from '@/configuration/sources/SentryDomains.yml'
import SentryConf from '@/configuration/sources/Sentry.yml'

let sentryHub, shopId

const reportEnabled = () => {
  return process.env.KR_SENTRY_ENABLED && sentryHub !== null
}

export const setupSentry = app => {
  if (reportEnabled()) {
    const sentryConfig = {
      unique: app === 'host',
      dsn: '%%sentryurl%%',
      release: '%%library_version%%',
      dist: '%%build_number%%',
      environment: '%%sentry_environment%%',
      autoSessionTracking: false, // disable session tracking (one per load)
      tracingOptions: {
        trackComponents: true
      },
      // integrations left to lyra-common-sentry-client's own defaults, which match its bundled @sentry SDK version
      beforeSend(event) {
        // Custom domain filter
        if (event.level === 'error') {
          if (event.request?.url) {
            const url = event.request.url
            for (const reg of SentryDomains.blacklisted) {
              const rgx = new RegExp(reg, 'g')
              if (rgx.test(url)) {
                return null
              }
            }
          }
        }

        return event
      }
    }
    // For host environment, only certain domains are allowed
    if (app === 'host') sentryConfig['allowUrls'] = SentryDomains.whitelisted
    try {
      sentryHub = LyraSentryClient.init(sentryConfig)
      setLocationData()
    } catch (error) {
      sentryHub = null
      console.error(`[KJS][${app}] Sentry could not be initialized`)
      console.error(error)
    }
  }
}

// Sentry v10 rejects tags with empty/undefined/null values ('expected a non-empty value')
const setTag = (key, value) => {
  if (value !== undefined && value !== null && value !== '') {
    sentryHub.setTag(key, value)
  }
}

export const logSentry = (prefix, message, byShopId = true) => {
  if (reportEnabled()) {
    // Report only for specific shops
    const shopIdList = []
    if (!byShopId || ~shopIdList.indexOf(shopId)) {
      if (_.isObject(message)) message = JSON.stringify(message)
      if (_.isObject(prefix)) prefix = JSON.stringify(prefix)
      sentryHub.captureMessage(`${prefix} : ${message}`)
    }
  }
}

export const logSentryException = (error, location, details = null) => {
  if (reportEnabled()) {
    sentryHub.setExtra('caught', location)
    if (details) sentryHub.setExtra('details', details)
    if (_.isObject(error) && error.type === 'error') {
      setTag('errorCode', error.errorCode)
      setTag('errorMessage', error.errorCode)
      setTag('detailedErrorCode', error.detailedErrorCode)
      setTag('detailedErrorMessage', error.detailedErrorMessage)
      const answer = error.metadata?.answer
      setTag('orderStatus', answer?.clientAnswer?.orderStatus)
      setTag('orderCycle', answer?.clientAnswer?.orderCycle)
      setTag('serverDate', answer?.clientAnswer?.serverDate)
      setTag('orderId', answer?.clientAnswer?.orderDetails?.orderId)
      sentryHub.setExtra('children', error.children)
      if (!_.isEmpty(error.metadata)) {
        if (error.metadata?.formToken) delete error.metadata.formToken
        sentryHub.setExtra('metadata', error.metadata)
      }

      // Group errors based on their code
      sentryHub.setFingerprint([error.errorCode])
      sentryHub.captureException(
        new Error(`${error.errorCode} : ${error.errorMessage}`)
      )
    } else if (_.isString(error)) {
      sentryHub.captureException(new Error(error))
    } else if (error instanceof Error) {
      sentryHub.setExtra('details', {
        message: error.toString(),
        stack: error.stack
      })
      sentryHub.captureException(error)
    }
  }
}

export const setSentryBasicInfo = state => {
  if (reportEnabled()) {
    const updateObj = JSON.parse(JSON.stringify(state))
    const allowed = _.union(SentryConf.tags, SentryConf.extras)
    const intProps = _.intersection(Object.keys(updateObj), allowed)
    if (intProps.length) {
      for (const prop of intProps) {
        if (prop in updateObj) {
          if (prop === 'formToken' && _.isString(updateObj[prop]))
            updateObj[prop] = updateObj[prop].substr(0, 16)
          if (prop === 'shopId') shopId = updateObj[prop]

          if (~SentryConf.tags.indexOf(prop)) setTag(prop, updateObj[prop])
          else sentryHub.setExtra(...cleanData(prop, updateObj[prop]))
        }
      }
    }
  }
}

const cleanData = (prop, data) => {
  if (prop === 'appLoadTimeData') {
    for (const item of data) {
      delete item.start
      delete item.end
      delete item.hide
      delete item.name
    }
  }

  return [prop, data]
}

export const setSentryTag = (tagName, tagValue) => {
  if (reportEnabled()) {
    setTag(tagName, tagValue)
  }
}

export const setSentryExtra = (extraName, extraValue) => {
  if (reportEnabled()) {
    sentryHub.setExtra(extraName, extraValue)
  }
}

export const setDeviceData = ({ state }) => {
  if (reportEnabled()) {
    const { browser, os } = state
    sentryHub.setContext('browser', browser)
    sentryHub.setContext('os', os)
  }
}

const setLocationData = () => {
  setSentryTag('url', `${location.origin}${location.pathname}`)
  setSentryExtra('href', location.href)
}
